import { AccountFacade } from '@mode/accounts/contract';
import {
  getAppName,
  getOwnerUsername,
  getSupportEmail,
  getThoughtspotIntegrated,
  HelpDocsService,
} from '@mode/shared/util-js';
import * as angular from 'angular';

interface DatasourceMetadata {
  linkUrl: string;
  datasourceName: string;
  provider: string;
  vendor: string;
  tlsOptional: boolean;
  tlsSupported: boolean;
  firewallConfigSuggested: boolean;
  rdsSupported: boolean;
  isBridged: boolean;
}

class DbHelpPanelController {
  readonly appName = getAppName();
  readonly intercomEmail = getSupportEmail();
  private datasource: DatasourceMetadata[];
  private currentView: string;
  private securityLink: string;
  private privacyLink: string;
  private userCanInvite: boolean;
  private fromSignUpFlow: boolean;
  private org;
  private hasStudioSeatLimit = false;
  private orgSeatLimitReached = false;
  private isAnalystStudio: boolean;

  static $inject = ['$window', 'AccountModel', 'AccountFacade', 'HelpDocsService'];

  constructor(
    private $window: angular.IWindowService,
    private AccountModel,
    private accountFacade: AccountFacade,
    public helpDocs: HelpDocsService
  ) {
    this.accountFacade.hasStudioSeatLimit$.subscribe((value) => (this.hasStudioSeatLimit = value));
    this.accountFacade.hasOrgSeatLimitReached$.subscribe((value) => (this.orgSeatLimitReached = value));
    this.isAnalystStudio = getThoughtspotIntegrated(this.$window.document);
  }

  $onInit() {
    const orgName = getOwnerUsername(this.$window.document);
    this.AccountModel.find({
      username: orgName,
      useEntryPath: true,
      refresh: true,
    }).then((organization) => {
      this.org = organization;
    });
  }

  showHelpAtTop() {
    if (this.userCanInvite) {
      return false;
    }
    if (this.currentView === 'select') {
      return true;
    }

    if (this.currentView === 'dc') {
      const bridgeAndTls =
        this.datasource['isBridged'] && this.datasource['tlsSupported'] && this.datasource['tlsOptional'];

      if (this.datasource['firewallConfigSuggested'] || bridgeAndTls) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  }

  showIntercomLink(): boolean {
    if (this.org) {
      return this.org.plan_code && this.org.plan_code !== 'free';
    }
    return false;
  }
}

const DbHelpPanelComponent: angular.IComponentOptions = {
  controller: DbHelpPanelController,
  controllerAs: 'ctrl',
  bindings: {
    currentView: '@',
    datasource: '<',
    securityLink: '@',
    privacyLink: '@',
    userCanInvite: '<',
    fromSignUpFlow: '<',
  },
  template: `
    <div class="db-help-panel">
      <div ng-if="ctrl.orgSeatLimitReached" class="studio-seat-prompt-body db-page db-invite-margin">
        <mode-seat-limit
          [paid-plan]="!ctrl.hasStudioSeatLimit"
          [db_page]="true"
          [has-no-secondary]="true"
        ></mode-seat-limit>
      </div>

      <div ng-if="ctrl.currentView === 'dc'" class= "db-invite-margin">
        <direct-connect
          datasource="ctrl.datasource"
        ></direct-connect>
      </div>

      <div ng-if="ctrl.currentView === 'bridge'" class="bridge-content db-invite-margin">
        <h2 class="help-panel-title">What is the Bridge connector?</h2>
        <ul class="bridge-connector-list">
          <li><span class="bridge-list-text">The Bridge connector is a lightweight, secure utility you'll install behind your firewall</span></li>
          <li><span class="bridge-list-text">When you write a query in {{ctrl.appName}} and click "run," the Bridge sends the request to your database to execute the computation</span></li>
          <li><span class="bridge-list-text">Once complete, the Bridge sends the data back to {{ctrl.appName}} so you can visualize and share the results</span></li>
        </ul>
      </div>

      <div ng-if="ctrl.userCanInvite" class="invite-teammate">
        <div ng-if="!(ctrl.orgSeatLimitReached)">
          <h2 class="help-panel-title">Don't have database credentials?</h2>
          <p class="help-panel-text">Invite a teammate who does.  We’ll send information they need to connect to {{ctrl.appName}}.</p>
          <db-invite-inline-form></db-invite-inline-form>
        </div>
      </div>

      <div class="questions-content">
        <div ng-class="{'position-top': ctrl.showHelpAtTop()}">
          <h2 class="help-panel-title" ng-if="ctrl.orgSeatLimitReached">Have connection questions?</h2>
          <div ng-if="ctrl.currentView === 'select'">
            <p class="help-panel-text">Learn about <a href="{{ ctrl.helpDocs.links['https://mode.com/help/articles/connecting-mode-to-your-database'] }}" target="_blank" rel="noopener">
            connecting your data to {{ctrl.appName}}</a>.</p>
            <p class="help-panel-text">{{ctrl.appName}} is serious about data security and privacy for everyone who connects. To learn more, read our
              <a href="{{ ctrl.securityLink }}" target="_blank" rel="noopener">security guidelines</a> or
              <a href="{{ ctrl.privacyLink }}" target="_blank" rel="noopener">privacy policy</a>.
            </p>
          </div>

          <div ng-if="ctrl.currentView === 'dc'">
            <p ng-if="ctrl.datasource.linkUrl" class="help-panel-text"><a href="{{ ctrl.datasource.linkUrl }}" target="_blank" rel="noopener">Connecting to {{ ctrl.datasource.datasourceName }}</a></p>
            <p class="help-panel-text">
              <a ng-href="{{ ctrl.datasource.isBridged ? ctrl.helpDocs.links['https://mode.com/help/articles/connecting-mode-to-your-database/#do-i-need-to-run-several-bridge-connectors-to-connect-mode-to-several-databases?'] : ctrl.helpDocs.links['https://mode.com/help/articles/connecting-mode-to-your-database/#faqs'] }}" target="_blank" rel="noopener">
                Troubleshoot common database connection issues
              </a>
            </p>
            <span ng-if="!ctrl.isAnalystStudio">
              <p class="help-panel-text"><a href="{{ ctrl.privacyLink }}" target="_blank" rel="noopener">Privacy policy</a></p>
              <p class="help-panel-text"><a href="{{ ctrl.securityLink }}" target="_blank" rel="noopener">Security guidelines</a></p>
            </span>
          </div>
        </div>

        <div ng-if="ctrl.currentView === 'bridge'">
          <p class="help-panel-text"><a href="{{ ctrl.helpDocs.links['https://mode.com/help/articles/connecting-mode-to-your-database/#bridge'] }}" target="_blank" rel="noopener">Connecting via Bridge</a></p>
          <p class="help-panel-text"><a href="{{ ctrl.helpDocs.links['https://mode.com/help/articles/connecting-mode-to-your-database/#do-i-need-to-run-several-bridge-connectors-to-connect-mode-to-several-databases?'] }}" target="_blank" rel="noopener">Troubleshoot common database connection issues</a></p>
          <p class="help-panel-text"><a href="{{ ctrl.privacyLink }}" target="_blank" rel="noopener">Privacy policy</a></p>
          <p class="help-panel-text"><a href="{{ ctrl.securityLink }}" target="_blank" rel="noopener">Security guidelines</a></p>
        </div>

        <p class="help">Our database connection team is here to help.
          <a ng-if="ctrl.showIntercomLink()" href intercom-show intercom-email="{{ctrl.intercomEmail}}" disable-on-show="true" class="intercom-link db-flow-intercom-link"><i class="icon-chat-white background-icon"></i></a>
          <a ng-if="!ctrl.showIntercomLink()" href="{{ ctrl.helpDocs.links['https://mode.com/help/articles/contact-us/'] }}" target="_blank" rel="noopener">Get in touch.</a>
        </p>
      </div>
    </div>
  `,
};

export { DatasourceMetadata, DbHelpPanelComponent };

angular.module('mode.components').component('dbHelpPanel', DbHelpPanelComponent);
